/* ============================================================
   LH CART DRAWER V1 (MVP)
   Vanilla JS, no jQuery, no frameworks. Defer-loaded.

   Public API: window.LHCartDrawer = { open, close, refresh, addToCart }

   Triggers:
     - document `theme:cart:popdown` event (fired by Pipeline's
       ProductAddButton AND assets/pdp-bundle-widget.js)
     - Click on a.lh-nav-cart (replaces /cart navigation)
     - Programmatic LHCartDrawer.open()

   Data source: /cart.js on open and after every write.
   Upsell source: window.LH_BUNDLE_CONTEXT (M3 wires this).

   This file is the M1 scaffold:
     - Bindings (open/close, Esc, overlay, nav cart, popdown event)
     - Fetch/render pipeline stubs that will be implemented in M2+
     - Body scroll lock
     - Global write-lock helper (used from M2 onward)
============================================================ */
(function () {
  'use strict';

  /* ---------- selectors ---------- */
  var SEL = {
    overlay:        '[data-lh-overlay]',
    drawer:         '[data-lh-drawer]',
    closeBtn:       '[data-lh-close]',
    body:           '[data-lh-body]',
    error:          '[data-lh-error]',
    headingCart:    '[data-lh-heading-cart]',
    headingUpsell:  '[data-lh-heading-upsell]',
    lineItems:      '[data-lh-line-items]',
    upsells:        '[data-lh-upsells]',
    empty:          '[data-lh-empty]',
    footer:         '[data-lh-footer]',
    subtotal:       '[data-lh-subtotal]',
    checkoutAmount: '[data-lh-checkout-amount]',
    navCart:        'a.lh-nav-cart'
  };

  /* ---------- elements (resolved on init) ---------- */
  var el = {};

  /* ---------- state ---------- */
  var state = {
    cart: null,           // raw /cart.js response
    isOpen: false,
    writeInFlight: false, // global write-lock
    writeQueue: [],       // FIFO of queued writes
    fetchInFlight: false, // in-flight /cart.js de-dup flag
    previousFocus: null   // focus restore target on close
  };

  /* ============================================================
     DOM helpers
  ============================================================ */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function show(node) { if (node) node.removeAttribute('hidden'); }
  function hide(node) { if (node) node.setAttribute('hidden', ''); }

  /* ============================================================
     Write lock — toggles drawer-wide disabled visual state.
     M1 wires the helper; M2 onward calls runWrite() for every POST.
  ============================================================ */
  function setLocked(locked) {
    state.writeInFlight = !!locked;
    if (!el.drawer) return;
    if (locked) {
      el.drawer.setAttribute('data-lh-locked', 'true');
    } else {
      el.drawer.removeAttribute('data-lh-locked');
    }
  }

  // Wraps an async POST so that concurrent calls queue rather than overlap.
  // Returns a Promise that resolves with the wrapped fn's resolution.
  function runWrite(fn) {
    return new Promise(function (resolve, reject) {
      state.writeQueue.push({ fn: fn, resolve: resolve, reject: reject });
      drainQueue();
    });
  }

  function drainQueue() {
    if (state.writeInFlight) return;
    var next = state.writeQueue.shift();
    if (!next) return;
    setLocked(true);
    Promise.resolve()
      .then(next.fn)
      .then(function (result) {
        setLocked(false);
        next.resolve(result);
        drainQueue();
      })
      .catch(function (err) {
        setLocked(false);
        next.reject(err);
        drainQueue();
      });
  }

  /* ============================================================
     Cart fetch / render pipeline
     M1: fetch on open and stub-render (line items HTML is M2).
  ============================================================ */
  function fetchCart() {
    // De-dup: a single in-flight fetch is enough; subsequent callers piggyback
    if (state.fetchInFlight) return state.fetchInFlight;
    var p = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        state.cart = cart;
        state.fetchInFlight = false;
        return cart;
      })
      .catch(function (err) {
        state.fetchInFlight = false;
        throw err;
      });
    state.fetchInFlight = p;
    return p;
  }

  function render() {
    if (!state.cart) return;
    var hasItems = state.cart.item_count > 0;

    if (hasItems) {
      hide(el.empty);
      show(el.footer);
      el.lineItems.innerHTML = state.cart.items.map(renderLineItem).join('');
      var subtotal = formatMoneyAud(state.cart.total_price);
      var subtotalBare = formatMoneyPlain(state.cart.total_price);
      el.subtotal.textContent = subtotal;
      el.checkoutAmount.textContent = subtotalBare;
      renderUpsells();
    } else {
      // Empty cart: only show the empty-state message + Continue shopping.
      // No line items, no footer, and no upsells (heading + cards both hidden)
      // even if a PDP LH_BUNDLE_CONTEXT is defined.
      show(el.empty);
      hide(el.footer);
      hide(el.headingUpsell);
      el.lineItems.innerHTML = '';
      el.upsells.innerHTML = '';
    }
  }

  /* ============================================================
     Upsell evaluation + render (M4 Standard + Priority; M5 Solution)
  ============================================================ */
  function renderUpsells() {
    var ctx = window.LH_BUNDLE_CONTEXT;
    if (!ctx) {
      hide(el.headingUpsell);
      el.upsells.innerHTML = '';
      return;
    }

    // Build cart membership set for cart-presence checks
    var inCart = {};
    if (state.cart && state.cart.items) {
      state.cart.items.forEach(function (i) { inCart[i.variant_id] = true; });
    }

    var cards = [];

    if (ctx.mode === 'standard') {
      cards = evaluateStandardCards(ctx, inCart);
    } else if (ctx.mode === 'solution') {
      var solutionCard = renderSolutionCard(ctx);
      if (solutionCard) cards = [solutionCard];
    }

    if (cards.length === 0) {
      hide(el.headingUpsell);
      el.upsells.innerHTML = '';
      return;
    }

    el.headingUpsell.textContent = ctx.sectionHeading || 'Complete Your Job';
    show(el.headingUpsell);
    el.upsells.innerHTML = cards.join('');
  }

  function evaluateStandardCards(ctx, inCart) {
    var cards = [];
    var TIER_ORDER = { ESSENTIAL: 0, RECOMMENDED: 1, UPSELL: 2 };

    /* Anchor = the PDP's own product. It counts as "in cart" if ANY line item
       is that product, matched by HANDLE - not by the single anchorVariantId,
       which is frozen at the first-available variant at page render. Keying on
       the variant id left the "Don't forget" card showing whenever the customer
       added a non-default size (e.g. Colour Cream 500ml), because that line's
       variant id never matched the frozen one. Handle is present on every cart
       line regardless of which size variant was chosen. */
    var anchorInCart = !!ctx.anchorHandle && !!(state.cart && state.cart.items) &&
      state.cart.items.some(function (i) { return i.handle === ctx.anchorHandle; });
    if (!anchorInCart && ctx.anchorVariantId) {
      cards.push(renderPriorityCard(ctx));
    }

    var slotCandidates = (ctx.slots || [])
      .map(function (slot, idx) { return { slot: slot, idx: idx }; })
      .filter(function (x) {
        return x.slot
          && x.slot.available === true
          && !inCart[x.slot.variantId];
      })
      .sort(function (a, b) {
        var ta = TIER_ORDER[a.slot.tier];
        var tb = TIER_ORDER[b.slot.tier];
        if (ta == null) ta = 99;
        if (tb == null) tb = 99;
        if (ta !== tb) return ta - tb;
        return a.idx - b.idx;
      });

    slotCandidates.forEach(function (x) {
      cards.push(renderStandardCard(x.slot));
    });

    // Cap: 3 if priority card is in play, otherwise 2
    var cap = (cards.length > 0 && !anchorInCart && ctx.anchorVariantId) ? 3 : 2;
    return cards.slice(0, cap);
  }

  function renderPriorityCard(ctx) {
    var img = ctx.anchorImg
      ? '<img class="lh-upsell-img" src="' + escapeAttr(ctx.anchorImg) + '" alt="' + escapeAttr(ctx.anchorTitle || '') + '" loading="lazy">'
      : '<div class="lh-upsell-img" aria-hidden="true"></div>';
    // Priority card has no Add button: anchor variant may need user selection
    // (colour, size, etc.) so we route the user back to the PDP to configure.
    // No price either — this is a "go back" action, not a buy decision.
    return ''
      + '<article class="lh-upsell-card lh-upsell-card--priority">'
      +   img
      +   '<div class="lh-upsell-info">'
      +     '<span class="lh-upsell-tag">Don&rsquo;t forget</span>'
      +     '<p class="lh-upsell-step">Don&rsquo;t forget your ' + escapeHtml(ctx.anchorTitle || '') + '</p>'
      +     '<div class="lh-upsell-action">'
      // NEEDS MUM REVIEW
      +       '<button type="button" data-lh-priority-close>Return to Product Page</button>'
      +     '</div>'
      +   '</div>'
      + '</article>';
  }

  function renderSolutionCard(ctx) {
    var target = ctx.solutionTarget;
    if (!target || !target.url) return '';
    var img = target.img
      ? '<img class="lh-upsell-img" src="' + escapeAttr(target.img) + '" alt="' + escapeAttr(target.title || '') + '" loading="lazy">'
      : '<div class="lh-upsell-img" aria-hidden="true"></div>';
    return ''
      + '<article class="lh-upsell-card lh-upsell-card--solution">'
      +   img
      +   '<div class="lh-upsell-info">'
      +     '<span class="lh-upsell-tag">See the full routine</span>'
      +     '<p class="lh-upsell-step">The full ' + escapeHtml(target.title || '') + ' routine</p>'
      +     '<p class="lh-upsell-copy">Configure cleaner, conditioner and protector together.</p>'
      +     '<div class="lh-upsell-action">'
      +       '<a href="' + escapeAttr(target.url) + '" class="lh-upsell-add" data-lh-solution-cta>See the kit</a>'
      +     '</div>'
      +   '</div>'
      + '</article>';
  }

  function renderStandardCard(slot) {
    // Revision Pass 1 Change 4: italic tagline (slot.headline) removed from
    // drawer render. PDP bundle widget still emits its own card_headline via
    // pdp-bundle-card.liquid, so the PDP visual is unaffected. The Liquid
    // emission of drawer_headline in pdp-bundle-widget.liquid is left as
    // dead JSON data (a few bytes per slot) for trivial restoration if needed.
    var img = slot.img
      ? '<img class="lh-upsell-img" src="' + escapeAttr(slot.img) + '" alt="' + escapeAttr(slot.title || '') + '" loading="lazy">'
      : '<div class="lh-upsell-img" aria-hidden="true"></div>';
    var pill = slot.pillLabel
      ? '<span class="lh-upsell-tag">' + escapeHtml(slot.pillLabel) + '</span>'
      : '';
    var body = slot.body
      ? '<p class="lh-upsell-copy">' + escapeHtml(slot.body) + '</p>'
      : '';
    var price = (slot.priceCents != null)
      ? formatMoneyAud(slot.priceCents)
      : (slot.priceFormatted || '');
    return ''
      + '<article class="lh-upsell-card" data-lh-upsell-variant="' + slot.variantId + '">'
      +   img
      +   '<div class="lh-upsell-info">'
      +     pill
      +     '<p class="lh-upsell-name">' + escapeHtml(slot.title || '') + '</p>'
      +     body
      +     '<div class="lh-upsell-action">'
      +       '<span class="lh-upsell-price">' + escapeHtml(price) + '</span>'
      +       '<button class="lh-upsell-add" type="button" data-lh-upsell-add>' + escapeHtml(slot.ctaLabel || 'Add') + '</button>'
      +     '</div>'
      +   '</div>'
      + '</article>';
  }

  /* ============================================================
     Add upsell (called via click on .lh-upsell-add inside drawer)
     Visual: button text becomes "Adding...", card fade-collapses
     for 200ms after success, then refresh re-renders upsells with
     the just-added one filtered out.
  ============================================================ */
  function addUpsell(variantId, cardEl, btnEl) {
    clearError();
    var originalLabel = btnEl ? btnEl.textContent : '';
    if (btnEl) {
      btnEl.disabled = true;
      btnEl.textContent = 'Adding...';
    }
    return runWrite(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            var msg = (result.data && (result.data.description || result.data.message)) || 'Could not add to cart.';
            showError(msg);
            if (btnEl) {
              btnEl.disabled = false;
              btnEl.textContent = originalLabel;
            }
            return null;
          }
          // Fade-collapse the card before re-render (matches preview spec)
          return new Promise(function (resolve) {
            if (cardEl) {
              var h = cardEl.offsetHeight;
              cardEl.style.maxHeight = h + 'px';
              // Force reflow so the transition picks up the start state
              void cardEl.offsetHeight;
              cardEl.setAttribute('data-lh-fading', 'true');
            }
            setTimeout(resolve, 220);
          })
            .then(refresh);
        });
    });
  }

  function renderLineItem(item, index, allItems) {
    // index is 0-based; Shopify /cart/change.js takes 1-based `line`
    var line = index + 1;
    var siblings = allItems || (state.cart && state.cart.items) || [];
    var imgSrc = item.image
      ? sizedImage(item.image, 144)
      : '';
    /* Subtitle = variant_title (if not Default Title) + line item property
       values, joined by " / ". Variant options come from Shopify's
       variant_title; non-variant selections (e.g. Colour, Topcoat,
       Globo-managed selections) live in item.properties. Private
       properties (key starts with "_") are skipped per Shopify
       convention. */
    var variantParts = [];
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantParts.push(item.variant_title);
    }
    var propsEntries = [];
    if (Array.isArray(item.properties)) {
      propsEntries = item.properties;
    } else if (item.properties && typeof item.properties === 'object') {
      propsEntries = Object.keys(item.properties).map(function (k) {
        return [k, item.properties[k]];
      });
    }
    /* --- Topcoat add-on finish relocation -------------------------------
       When ADD TOPCOAT is selected, Globo splits it into a second cart line
       (product title "ADD TOPCOAT ...") linked to its parent by a group id:
       the parent carries _gpo_product_group, the add-on line carries a
       matching _gpo_parent_product_group. The chosen FINISH is stored only
       on the PARENT (a property whose value is Matte/Satin/Gloss/High Gloss).
       We move that finish onto the topcoat line and drop it from the parent,
       so the parent reads "size / colour" and the topcoat line reads
       "size / finish".

       Pairing is by group-id equality only, never by product name, so it is
       unaffected by any product being archived and never crosses two
       different products' topcoats in one cart (group ids are unique per
       cart line, verified). Bundled-topcoat products (e.g. R&R kit) have NO
       ADD TOPCOAT child line, so their finish is left on the parent. */
    var FINISH_NAMES = ['matte', 'satin', 'gloss', 'high gloss'];
    function readProp(it, name) {
      if (!it || !it.properties) return null;
      if (Array.isArray(it.properties)) { for (var i = 0; i < it.properties.length; i++) { if (it.properties[i][0] === name) return it.properties[i][1]; } return null; }
      return it.properties[name] != null ? it.properties[name] : null;
    }
    function finishNameOf(value) {
      if (value == null) return null;
      var v = String(value).split(/\s+[–—-]\s+/)[0].trim();
      return FINISH_NAMES.indexOf(v.toLowerCase()) !== -1 ? v : null;
    }
    function parentFinishFor(parentGroup) {
      for (var i = 0; i < siblings.length; i++) {
        if (readProp(siblings[i], '_gpo_product_group') !== parentGroup) continue;
        var p = siblings[i].properties;
        var entries = Array.isArray(p) ? p : Object.keys(p || {}).map(function (k) { return [k, p[k]]; });
        for (var j = 0; j < entries.length; j++) { if (String(entries[j][0]).charAt(0) === '_') continue; var fn = finishNameOf(entries[j][1]); if (fn) return fn; }
      }
      return null;
    }
    var myGroup = readProp(item, '_gpo_product_group');
    var myParentGroup = readProp(item, '_gpo_parent_product_group');
    var isTopcoatAddon = !!myParentGroup && /ADD\s*TOPCOAT/i.test(item.product_title || '');
    var parentHasTopcoatChild = !!myGroup && siblings.some(function (c) {
      return readProp(c, '_gpo_parent_product_group') === myGroup && /ADD\s*TOPCOAT/i.test(c.product_title || '');
    });
    /* Show the customer's real selections (colour + topcoat finish) and hide
       the add-on clutter. We key off NOTHING product-specific because Globo
       reuses generic keys inconsistently: e.g. the topcoat SIZE add-on is
       'dropdown-1' on colour-cream-85 but 'dropdown-2' on colour-cream-kit,
       and 'dropdown-2' is the topcoat FINISH on three other products. So the
       size-vs-finish call cannot be made by key; it is made by value.

       Two filters, a property must pass both to be shown:

         A. Add-on key denylist. filler2/glue2 attach as line-item properties
            ("None" when unselected); accessories/after-care spawn their own
            line items but are denied defensively. Everything else (every
            colour key: image-dropdown-1 / color-dropdown-1 / colour /
            "colour choice", and every finish key: topcoat / dropdown-2 /
            dropdown-3) flows through.

         B. Measurement-value test. The topcoat SIZE add-on shares a generic
            dropdown-N key with the finish, so it is hidden by VALUE: any pure
            size token (250ml, 500ml, 1L, 10gm, 1kg) is dropped. Colour names
            and finish names ("Matte - extra flat...") never match the anchored
            pattern, so they are always shown. */
    var DENY_KEYS = ['filler2', 'glue2', 'accessories', 'accessories leather paint', 'after care kit', 'image-swatches-1', 'image-swatches-2', 'image-swatches-3'];
    var SIZE_VALUE = /^\s*\d+(\.\d+)?\s*(kg|gm|ml|g|l)\s*$/i;
    propsEntries.forEach(function (pair) {
      var key = pair[0];
      var value = pair[1];
      if (typeof key === 'string' && key.charAt(0) === '_') return;
      /* Skip empty values AND skip "None" (Globo's default for unselected addons). */
      if (value == null || value === '' || value === 'None') return;
      var normalizedKey = String(key).toLowerCase().trim();
      /* Filter A: add-on key denylist (exact, plus defensive prefixes). */
      if (DENY_KEYS.indexOf(normalizedKey) !== -1) return;
      if (/^image-swatches/.test(normalizedKey) || /^accessor/.test(normalizedKey) || /^after care/.test(normalizedKey)) return;
      /* Filter B: hide pure measurement values (the topcoat SIZE add-on, and
         any selected filler/glue). Tested on the raw value, before the
         "Name - description" split, so a long finish label can never match. */
      if (SIZE_VALUE.test(String(value))) return;
      /* Strip "Name - description" / "Name – description" / "Name — description"
         convention used by Globo selectors. Drawer subtitle shows only the
         Name portion; the full raw value still flows to Shopify and the order. */
      var displayValue = String(value).split(/\s+[–—-]\s+/)[0].trim();
      if (!displayValue) return;
      /* If this parent product has its own ADD TOPCOAT line, the finish moves
         to that line: drop it here so the parent shows size + colour only. */
      if (parentHasTopcoatChild && FINISH_NAMES.indexOf(displayValue.toLowerCase()) !== -1) return;
      variantParts.push(displayValue);
    });
    /* Topcoat add-on line carries no public properties of its own: show its
       size (variant_title) plus the finish read from its paired parent line. */
    if (isTopcoatAddon) {
      var movedFinish = parentFinishFor(myParentGroup);
      if (movedFinish) variantParts.push(movedFinish);
    }
    var variantLabel = variantParts.join(' / ');
    var lineTotal = formatMoneyPlain(item.final_line_price);
    var qty = item.quantity;
    var minusDisabled = qty <= 1 ? 'disabled aria-disabled="true"' : '';

    return ''
      + '<div class="lh-line-item" data-lh-line="' + line + '" data-lh-key="' + escapeAttr(item.key) + '">'
      +   (imgSrc ? '<img class="lh-line-img" src="' + escapeAttr(imgSrc) + '" alt="' + escapeAttr(item.product_title) + '" loading="lazy">' : '<div class="lh-line-img" aria-hidden="true"></div>')
      +   '<div class="lh-line-info">'
      +     '<div class="lh-line-name">' + escapeHtml(item.product_title) + '</div>'
      +     (variantLabel ? '<div class="lh-line-variant">' + escapeHtml(variantLabel) + '</div>' : '')
      +     '<div class="lh-qty-control">'
      +       '<button class="lh-qty-btn" type="button" data-lh-qty="dec" aria-label="Decrease quantity" ' + minusDisabled + '>&minus;</button>'
      +       '<span class="lh-qty-num" data-lh-qty-num>' + qty + '</span>'
      +       '<button class="lh-qty-btn" type="button" data-lh-qty="inc" aria-label="Increase quantity">+</button>'
      +     '</div>'
      +   '</div>'
      +   '<div class="lh-line-meta">'
      +     '<div class="lh-line-price">$' + lineTotal + '</div>'
      +     '<button class="lh-line-remove" type="button" data-lh-remove>Remove</button>'
      +   '</div>'
      + '</div>';
  }

  function sizedImage(url, width) {
    // Shopify CDN images accept a `width` query param for responsive sizing.
    // Some image URLs already have query params; append correctly.
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function formatMoneyAud(cents) {
    return '$' + formatMoneyPlain(cents) + ' AUD';
  }
  function formatMoneyPlain(cents) {
    return (cents / 100).toFixed(2);
  }

  function showError(msg, withRetry) {
    if (!el.error) return;
    el.error.innerHTML = '';
    var span = document.createElement('span');
    span.textContent = msg;
    el.error.appendChild(span);
    if (withRetry) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lh-drawer-error-retry';
      btn.textContent = 'Retry';
      btn.addEventListener('click', function () {
        clearError();
        open();
      });
      el.error.appendChild(btn);
    }
    show(el.error);
  }
  function clearError() {
    if (!el.error) return;
    el.error.innerHTML = '';
    hide(el.error);
  }

  /* ============================================================
     Open / Close
  ============================================================ */
  function open() {
    clearError();
    if (!state.isOpen) {
      state.previousFocus = document.activeElement;
    }
    show(el.overlay);
    show(el.drawer);
    el.drawer.setAttribute('aria-hidden', 'false');
    // Force a paint before adding the class so the transition runs
    requestAnimationFrame(function () {
      el.overlay.classList.add('is-open');
      el.drawer.classList.add('is-open');
      // Focus the close button for keyboard users (after the slide-in starts)
      var closeBtn = el.drawer.querySelector(SEL.closeBtn);
      if (closeBtn) {
        try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); }
      }
    });
    document.body.style.overflow = 'hidden';
    document.body.classList.add('lh-cart-drawer-open');
    state.isOpen = true;

    fetchCart()
      .then(render)
      .catch(function (err) {
        console.error('[LHCartDrawer] cart fetch error:', err);
        // Show empty-state UI plus a retry button in the error slot.
        // "Your Cart" heading stays in the header band regardless.
        hide(el.footer);
        el.lineItems.innerHTML = '';
        showError("Couldn't load cart.", true);
      });
  }

  function close() {
    el.overlay.classList.remove('is-open');
    el.drawer.classList.remove('is-open');
    el.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('lh-cart-drawer-open');
    state.isOpen = false;
    clearError();
    // Restore focus to whatever was focused before drawer opened
    if (state.previousFocus && typeof state.previousFocus.focus === 'function') {
      try { state.previousFocus.focus({ preventScroll: true }); } catch (e) { state.previousFocus.focus(); }
    }
    state.previousFocus = null;
    // Hide elements after animation completes (CSS transition timing matches)
    setTimeout(function () {
      if (!state.isOpen) {
        hide(el.overlay);
        hide(el.drawer);
      }
    }, 320);
  }

  /* ============================================================
     Public addToCart (M4 wires this fully)
  ============================================================ */
  function addToCart(variantId, qty) {
    var q = qty || 1;
    return runWrite(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: q }] })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            var msg = (result.data && (result.data.description || result.data.message)) || 'Could not add to cart.';
            showError(msg);
            return null;
          }
          return refresh();
        });
    });
  }

  function refresh() {
    return fetchCart()
      .then(function (cart) {
        render();
        // Notify the rest of the theme (nav badge listens for this)
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          detail: { cart: cart }
        }));
        return cart;
      });
  }

  /* ============================================================
     Event bindings
  ============================================================ */
  function bind() {
    /* Defensive: the init-time cleanup above doesn't run for pages restored
       from the back/forward cache (scripts don't re-execute on bfcache
       restore). Strip the class on pageshow.persisted so back-button nav
       to a PDP from /checkout (or anywhere) doesn't inherit a stale class
       and accidentally hide the sticky ATC. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        document.body.classList.remove('lh-cart-drawer-open');
      }
    });

    // Close X / Continue shopping / Empty-state continue / Overlay
    document.addEventListener('click', function (e) {
      var closeTarget = e.target.closest && e.target.closest(SEL.closeBtn);
      if (closeTarget && el.drawer.contains(closeTarget)) {
        e.preventDefault();
        close();
        return;
      }
      if (e.target === el.overlay) {
        close();
      }
    });

    // Esc key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.isOpen) {
        close();
      }
    });

    // Nav cart icon — replace navigation with drawer open
    document.addEventListener('click', function (e) {
      var navCart = e.target.closest && e.target.closest(SEL.navCart);
      if (navCart) {
        e.preventDefault();
        open();
      }
    });

    // Pipeline + bundle widget both dispatch this on successful ATC
    document.addEventListener('theme:cart:popdown', function () {
      open();
    });

    /* ============================================================
       Globo / form-submit intercept (fixes the Globo ATC bug).

       Pipeline's ProductAddButton.onSuccess listener (theme.dev.js:3756+)
       is bound to the original native ATC button at section-init time.
       When Globo bootstraps, it hides the native button and renders a
       clone (.gpo-clone-atc-button). The user clicks the clone, which
       is type="submit" inside the same form but is NOT the element
       Pipeline's click handler is bound to — so Pipeline's
       preventDefault never runs, the form submits natively to
       /cart/add (form action from {% form 'product' %}), and Shopify
       302-redirects to /cart. Drawer never gets a chance.

       Fix: catch the form's submit event. If a click-level handler
       already preventDefaulted (path 1, no Globo), the submit event
       never fires per HTML spec, and this handler is silent. If the
       submit fires (path 2, Globo's clone), we own it: build FormData,
       POST to /cart/add.js, then dispatch theme:cart:popdown on
       document. Both the drawer (this file) and the toast
       (lh-cart-notification.js:35) listen on document and respond
       normally. Form line-item properties (properties[image-swatches-1][]
       etc.) survive the FormData round-trip so Globo addons land in
       the cart correctly.

       Bundle widget ATC does its own fetch + dispatch and never
       triggers a form submit, so this intercept is invisible to it.
    ============================================================ */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (!form.hasAttribute('data-product-form')) return;
      // Mirror Pipeline's own guard: skip forms with file inputs (cannot
      // be sent as application/x-www-form-urlencoded).
      if (form.querySelector('[type="file"]')) return;

      e.preventDefault();

      var formData = new FormData(form);
      var formString = new URLSearchParams(formData).toString();

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: formString
      })
        .then(function (r) {
          return r.json().then(function (data) {
            return { ok: r.ok, status: r.status, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            console.error('[LHCartDrawer] form-intercept add failed:', result.status, result.data);
            // Open the drawer anyway so the user can see current cart state +
            // surfaced error rather than getting silently stranded on the PDP.
            var msg = (result.data && (result.data.description || result.data.message))
              || 'Could not add to cart.';
            open();
            showError(msg);
            return;
          }
          // Same payload shape Pipeline uses for the toast title field.
          document.dispatchEvent(new CustomEvent('theme:cart:popdown', {
            detail: { variant: result.data },
            bubbles: true
          }));
        })
        .catch(function (err) {
          console.error('[LHCartDrawer] form-intercept fetch error:', err);
        });
    }, false);

    // Upsell Add + Solution CTA + Priority close (event delegation within upsells container)
    el.upsells.addEventListener('click', function (e) {
      // Mode A Solution Card: close drawer first then navigate
      var solutionCta = e.target.closest && e.target.closest('[data-lh-solution-cta]');
      if (solutionCta) {
        e.preventDefault();
        var url = solutionCta.getAttribute('href');
        close();
        setTimeout(function () { window.location.href = url; }, 320);
        return;
      }
      // Priority card: close drawer, no POST. User is already on the PDP
      // and may need to choose a variant before adding to cart.
      var priorityClose = e.target.closest && e.target.closest('[data-lh-priority-close]');
      if (priorityClose) {
        e.preventDefault();
        close();
        return;
      }
      // Standard Add
      if (state.writeInFlight) return;
      var addBtn = e.target.closest && e.target.closest('[data-lh-upsell-add]');
      if (!addBtn) return;
      var card = addBtn.closest('.lh-upsell-card');
      if (!card) return;
      var variantId = parseInt(card.getAttribute('data-lh-upsell-variant'), 10);
      if (!variantId) return;
      addUpsell(variantId, card, addBtn);
    });

    // Line item qty +/- and Remove (event delegation within line items container)
    el.lineItems.addEventListener('click', function (e) {
      if (state.writeInFlight) return; // double-safety; CSS already blocks pointer events
      var qtyBtn = e.target.closest && e.target.closest('[data-lh-qty]');
      var removeBtn = e.target.closest && e.target.closest('[data-lh-remove]');
      var lineItem = e.target.closest && e.target.closest('.lh-line-item');
      if (!lineItem) return;
      var line = parseInt(lineItem.getAttribute('data-lh-line'), 10);
      if (qtyBtn) {
        if (qtyBtn.disabled) return;
        var dir = qtyBtn.getAttribute('data-lh-qty');
        var current = parseInt(lineItem.querySelector('[data-lh-qty-num]').textContent, 10) || 0;
        var nextQty = dir === 'inc' ? current + 1 : current - 1;
        if (nextQty < 1) return; // can't drop below 1 via −; user must use Remove
        changeLine(line, nextQty, lineItem);
      } else if (removeBtn) {
        changeLine(line, 0, lineItem);
      }
    });
  }

  /* ============================================================
     /cart/change.js writes (qty change + remove)
  ============================================================ */
  function changeLine(line, quantity, lineItemEl) {
    clearError();
    if (lineItemEl) lineItemEl.setAttribute('data-lh-pending', 'true');
    return runWrite(function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            var msg = (result.data && (result.data.description || result.data.message)) || 'Could not update cart.';
            showError(msg);
            if (lineItemEl) lineItemEl.removeAttribute('data-lh-pending');
            return null;
          }
          state.cart = result.data;
          render();
          // After re-render the original DOM node is gone, no need to clear pending flag
          document.dispatchEvent(new CustomEvent('theme:cart:change', {
            detail: { cart: result.data }
          }));
          return result.data;
        });
    });
  }

  /* ============================================================
     Init
  ============================================================ */
  function init() {
    /* Defensive: clear any stale lh-cart-drawer-open class on the body.
       Hot-reload artifacts in `shopify theme dev` can leave the class on
       the body if the drawer was open when the dev server rebuilt assets.
       An errored close path or an unexpected navigation could also leave
       it persisting. Stripping it at init means a fresh page load is
       always in a clean state regardless of how we got here. */
    document.body.classList.remove('lh-cart-drawer-open');

    el.overlay        = $(SEL.overlay);
    el.drawer         = $(SEL.drawer);
    if (!el.drawer || !el.overlay) {
      // Snippet not rendered on this template, silently abort
      return;
    }
    el.body           = $(SEL.body, el.drawer);
    el.error          = $(SEL.error, el.drawer);
    el.headingCart    = $(SEL.headingCart, el.drawer);
    el.headingUpsell  = $(SEL.headingUpsell, el.drawer);
    el.lineItems      = $(SEL.lineItems, el.drawer);
    el.upsells        = $(SEL.upsells, el.drawer);
    el.empty          = $(SEL.empty, el.drawer);
    el.footer         = $(SEL.footer, el.drawer);
    el.subtotal       = $(SEL.subtotal, el.drawer);
    el.checkoutAmount = $(SEL.checkoutAmount, el.drawer);
    bind();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ============================================================
     Public API
  ============================================================ */
  window.LHCartDrawer = {
    open: open,
    close: close,
    refresh: refresh,
    addToCart: addToCart
  };
})();

/* ============================================================
   Universal /cart/add* interceptor.

   Wraps window.fetch and XMLHttpRequest.prototype.{open,send} so
   that any successful POST to /cart/add, /cart/add.js, or
   /cart/add-multiple.js auto-opens the drawer, regardless of
   which script in the page made the request.

   Why this is needed: Pipeline's native ATC dispatches
   theme:cart:popdown and the drawer's listener catches it. The
   Globo Product Options app posts via XHR but does NOT dispatch
   any event the drawer listens for. This wrapper covers Globo
   and any future third-party app that posts to a standard
   /cart/add* endpoint (which is required for the cart to update
   at all).

   Idempotency:
   - On the Pipeline path, the existing popdown dispatch opens
     the drawer first; this wrapper's setTimeout-deferred call
     hits an already-open drawer (visually no-op) and triggers
     one extra cheap /cart.js fetch via refresh(). Accepted.
   - On the bundle widget path, same behaviour: popdown wins
     the race, wrapper does a redundant refresh. Accepted.
   - On the Globo path, popdown never fires, so this wrapper's
     trigger is the only path that opens the drawer.

   Placement: separate IIFE outside the main drawer IIFE so it
   patches window.fetch and XMLHttpRequest.prototype synchronously
   on script load, before any user-triggered ATC can fire. The
   defer attribute on the script tag means we still run after
   DOM parse but before DOMContentLoaded.
============================================================ */
(function lhUniversalAtcInterceptor() {
  'use strict';

  var CART_ADD_RE = /\/cart\/add(-multiple)?(\.js)?(\?|$)/;

  function isCartAddUrl(url) {
    return typeof url === 'string' && CART_ADD_RE.test(url);
  }

  function isPostMethod(method) {
    return typeof method === 'string' && /^post$/i.test(method);
  }

  function triggerDrawer() {
    /* Defer to next macrotask so any popdown-based success handlers
       (Pipeline, bundle widget) run first. open() is idempotent so
       order doesn't strictly matter, but deferring keeps the trigger
       out of the request's resolution callback chain. */
    setTimeout(function () {
      if (!window.LHCartDrawer
          || typeof window.LHCartDrawer.refresh !== 'function'
          || typeof window.LHCartDrawer.open !== 'function') {
        return;
      }
      /* refresh() fetches /cart.js, re-renders, and dispatches
         theme:cart:change so the nav cart badge updates. Then open()
         shows the drawer. open() fires its own fetchCart() — a
         redundant fetch on the success path, but /cart.js is cheap
         and this keeps the code simple. open() is idempotent so
         calling it when the drawer is already open (e.g. after the
         Pipeline popdown listener already opened it) is a no-op. */
      window.LHCartDrawer.refresh()
        .then(function () { window.LHCartDrawer.open(); })
        .catch(function (err) {
          console.error('[LHCartDrawer] interceptor refresh failed:', err);
          try { window.LHCartDrawer.open(); } catch (e) {}
        });
    }, 0);
  }

  /* ---- fetch wrapper ---- */
  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = (init && init.method) || (input && input.method) || 'GET';
      var willHandle = isCartAddUrl(url) && isPostMethod(method);
      var promise = origFetch.apply(this, arguments);
      if (willHandle) {
        promise.then(
          function (response) {
            if (response && response.ok) triggerDrawer();
          },
          function () { /* network errors: leave the app's own error UI alone */ }
        );
      }
      return promise;
    };
  }

  /* ---- XMLHttpRequest wrapper ----
     Patches the prototype, not the constructor, so any XHR instance
     (including those held by libraries that captured XMLHttpRequest
     before our wrapper ran, e.g. Pipeline's axios) goes through our
     patched methods. */
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var origOpen = window.XMLHttpRequest.prototype.open;
    var origSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__lhMethod = method;
      this.__lhUrl = url;
      return origOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (isCartAddUrl(xhr.__lhUrl) && isPostMethod(xhr.__lhMethod)) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) triggerDrawer();
        });
      }
      return origSend.apply(this, arguments);
    };
  }
})();
